import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Fade, Tooltip, useTheme, alpha, keyframes } from '@mui/material';
import { GEMINI_GRADIENT } from '../theme';

const SHOW_AFTER_PX = 280;
const SCROLL_IDLE_MS = 180;

const riseHint = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

/**
 * Fixed bottom-right control to return to page top.
 * Fades while the user is scrolling, then restores once scrolling stops.
 */
export function ScrollToTopButton(): ReactNode {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [visible, setVisible] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reduceMotion = useRef(false);

  useEffect(() => {
    reduceMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const syncScrollState = useCallback(() => {
    const y = window.scrollY || document.documentElement.scrollTop;
    setVisible(y > SHOW_AFTER_PX);

    if (reduceMotion.current) {
      setScrolling(false);
      return;
    }

    setScrolling(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setScrolling(false), SCROLL_IDLE_MS);
  }, []);

  useEffect(() => {
    syncScrollState();
    window.addEventListener('scroll', syncScrollState, { passive: true });
    return () => {
      window.removeEventListener('scroll', syncScrollState);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [syncScrollState]);

  const goTop = () => {
    window.scrollTo({
      top: 0,
      behavior: reduceMotion.current ? 'auto' : 'smooth',
    });
  };

  const ring = isLight
    ? 'rgba(255,255,255,0.55)'
    : 'rgba(255,255,255,0.22)';
  const glow = isLight
    ? '0 10px 28px rgba(29, 78, 216, 0.38), 0 2px 8px rgba(30, 64, 175, 0.28)'
    : '0 12px 32px rgba(37, 99, 235, 0.55), 0 0 0 1px rgba(147, 197, 253, 0.25)';

  return (
    <Fade in={visible} timeout={280} unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          // Sits to the LEFT of the assistant launcher, which Layout also mounts fixed in
          // this corner (48px Fab at right/bottom 24). Stacking it vertically instead put
          // it underneath the open chat panel, which is anchored bottom: 88 — so the two
          // share a baseline and separate horizontally: 24 + 48 + 12px gap = 84.
          right: { xs: 76, md: 84 },
          // Same baseline as the launcher, and this button is 56px against the Fab's 48px,
          // so nudge down 4px to centre the two on each other.
          bottom: { xs: 16, md: 20 },
          zIndex: (t) => t.zIndex.snackbar + 1,
          opacity: scrolling ? 0.28 : 1,
          transform: scrolling ? 'scale(0.92) translateY(4px)' : 'scale(1) translateY(0)',
          transition: 'opacity 0.22s ease, transform 0.22s ease',
          pointerEvents: scrolling ? 'none' : 'auto',
          '@media (prefers-reduced-motion: reduce)': {
            opacity: 1,
            transform: 'none',
            transition: 'none',
            pointerEvents: 'auto',
          },
        }}
      >
        <Tooltip title="Back to top" placement="left" enterDelay={400}>
          <Box
            component="button"
            type="button"
            aria-label="Back to top"
            onClick={goTop}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.35,
              width: 56,
              height: 56,
              borderRadius: '18px',
              background: GEMINI_GRADIENT,
              boxShadow: glow,
              color: '#fff',
              position: 'relative',
              isolation: 'isolate',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 2,
                borderRadius: '16px',
                border: `1.5px solid ${ring}`,
                pointerEvents: 'none',
                zIndex: 0,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -3,
                borderRadius: '20px',
                background: alpha(theme.palette.primary.main, isLight ? 0.18 : 0.28),
                filter: 'blur(10px)',
                zIndex: -1,
                opacity: 0.9,
              },
              '&:hover': {
                transform: 'translateY(-3px) scale(1.04)',
                boxShadow: isLight
                  ? '0 14px 34px rgba(29, 78, 216, 0.48), 0 4px 12px rgba(30, 64, 175, 0.32)'
                  : '0 16px 40px rgba(37, 99, 235, 0.65), 0 0 0 1px rgba(191, 219, 254, 0.35)',
                '& .scroll-top-chevrons': {
                  animation: `${riseHint} 0.9s ease-in-out infinite`,
                },
              },
              '&:focus-visible': {
                outline: `3px solid ${alpha(theme.palette.primary.light, 0.9)}`,
                outlineOffset: 3,
              },
              '&:active': {
                transform: 'translateY(-1px) scale(0.98)',
              },
            }}
          >
            <Box
              className="scroll-top-chevrons"
              aria-hidden
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                lineHeight: 0,
                mt: 0.15,
                '& span': {
                  display: 'block',
                  width: 12,
                  height: 12,
                  borderLeft: '2.5px solid #fff',
                  borderTop: '2.5px solid #fff',
                  transform: 'rotate(45deg)',
                  marginBottom: '-7px',
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                },
              }}
            >
              <span />
              <span />
            </Box>
            <Box
              component="span"
              sx={{
                mt: 0.85,
                fontSize: '0.62rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                lineHeight: 1,
                textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                fontFamily: theme.typography.fontFamily,
              }}
            >
              Top
            </Box>
          </Box>
        </Tooltip>
      </Box>
    </Fade>
  );
}
